import {createStylesParams, StyleParamType, wixColorParam, wixFontParam} from '@wix/tpa-settings';
import {CheckoutButtonsPosition} from '@wix/wixstores-client-core';
import {StyleType} from '@wix/editor-platform-ooi-metadata-resolver';
import {StyleParam} from '../../common/constants';
import type {CustomCssVarsFn} from '@wix/yoshi-flow-editor';
import {toDisplayProperty} from '../../domain/utils/styleParams.utils';

type IStylesParams = {
  [StyleParam.CartBackground]: StyleParamType.Color;
  [StyleParam.CartDividersColor]: StyleParamType.Color;
  [StyleParam.CartTextColor]: StyleParamType.Color;
  [StyleParam.CartTextFontStyle]: StyleParamType.Font;
  [StyleParam.CartTitlesColor]: StyleParamType.Color;
  [StyleParam.CartTitlesFontStyle]: StyleParamType.Font;
  [StyleParam.CartLinksColor]: StyleParamType.Color;
  [StyleParam.CartDiscountNameColor]: StyleParamType.Color;
  [StyleParam.CartButtonBorderColor]: StyleParamType.Color;
  [StyleParam.CartButtonBackgroundColor]: StyleParamType.Color;
  [StyleParam.CartButtonTextColor]: StyleParamType.Color;
  [StyleParam.CartButtonTextFont]: StyleParamType.Font;
  [StyleParam.CheckoutButtonsPosition]: StyleParamType.Number;
  [StyleParam.ShowShipping]: StyleParamType.Boolean;
  [StyleParam.ShowTax]: StyleParamType.Boolean;
  [StyleParam.ShowContinueShopping]: StyleParamType.Boolean;
  [StyleParam.ShowCoupon]: StyleParamType.Boolean;
  [StyleParam.ShowBuyerNote]: StyleParamType.Boolean;
  [StyleParam.ShowExpressCheckout]: StyleParamType.Boolean;
  [StyleParam.sidePaddingSliderOnDesktop]: StyleParamType.Number;
  [StyleParam.CornerRadius]: StyleParamType.String;
  [StyleParam.CartButtonBorderSize]: StyleParamType.String;
};

export default createStylesParams<IStylesParams>({
  [StyleParam.CartBackground]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
        opacity: 0,
      },
    },
  },
  [StyleParam.CartTitlesColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  [StyleParam.CartTitlesFontStyle]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 20,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 15,
      },
    },
  },
  [StyleParam.CartTextColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  [StyleParam.CartTextFontStyle]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-M',
        size: 15,
      },
    },
  },
  [StyleParam.CartDividersColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.2),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
        opacity: 0.2,
      },
    },
  },
  [StyleParam.CartLinksColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
  },
  [StyleParam.CartDiscountNameColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
  },
  [StyleParam.CartButtonBorderColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'static',
        preset: 'color-5',
      },
    },
  },
  [StyleParam.CartButtonBackgroundColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'static',
        preset: 'color-8',
      },
    },
  },
  [StyleParam.CartButtonTextColor]: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: {
        type: 'static',
        preset: 'color-1',
      },
    },
  },
  [StyleParam.CartButtonTextFont]: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M'),
    metadata: {
      type: StyleType.font,
      defaultValue: {
        type: 'static',
        preset: 'Body-L',
        size: 16,
      },
    },
  },
  [StyleParam.CheckoutButtonsPosition]: {
    type: StyleParamType.Number,
    getDefaultValue: () => Number(CheckoutButtonsPosition.ABOVE_AND_BELOW_CART),
  },
  [StyleParam.ShowShipping]: {
    type: StyleParamType.Boolean,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  [StyleParam.ShowTax]: {
    type: StyleParamType.Boolean,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  [StyleParam.ShowContinueShopping]: {
    type: StyleParamType.Boolean,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: false,
      },
    },
  },
  [StyleParam.ShowCoupon]: {
    type: StyleParamType.Boolean,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  [StyleParam.ShowBuyerNote]: {
    type: StyleParamType.Boolean,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  [StyleParam.ShowExpressCheckout]: {
    type: StyleParamType.Boolean,
    metadata: {
      type: StyleType.booleanValue,
      defaultValue: {
        type: 'static',
        value: true,
      },
    },
  },
  [StyleParam.sidePaddingSliderOnDesktop]: {
    type: StyleParamType.Number,
    metadata: {
      type: StyleType.padding,
      defaultValue: {
        type: 'static',
        value: 20,
      },
    },
  },
  [StyleParam.CornerRadius]: {
    type: StyleParamType.String,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  [StyleParam.CartButtonBorderSize]: {
    type: StyleParamType.String,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
});

export const customCssVars: CustomCssVarsFn = ({styleParams: {booleans}}) => {
  return {
    expressCheckoutButtonDisplay: toDisplayProperty(booleans[StyleParam.ShowExpressCheckout]),
  };
};
